/**
 * Stock Chart Viewer - Alpha Vantage API
 */

import { state } from '../state.js';
import { API_KEY, API_BASE, USE_MOCK_DATA, PERIODS, CRYPTO_PERIODS, getCompanyName } from '../config.js';
import { generateMockData } from './mock.js';
import { getCachedData, setCachedData } from '../cache/storage.js';
import { incrementApiUsage } from '../cache/usage-tracker.js';

export async function fetchStockData(symbol, period = '1M', forceRefresh = false) {
    // デモモードの場合、モックデータを使用
    if (USE_MOCK_DATA || API_KEY === 'demo') {
        await new Promise(resolve => setTimeout(resolve, 300));
        return generateMockData(symbol, period);
    }

    // Check cache first
    const cached = getCachedData(symbol, period);

    if (!forceRefresh && cached) {
        return cached;
    }

    // Cache-only mode
    if (state.cacheOnlyMode && !forceRefresh) {
        if (cached) {
            return cached;
        }
        throw new Error('キャッシュ専用モードです。このシンボルのキャッシュデータがありません。');
    }

    const isCrypto = state.currentMode === 'CRYPTO';
    const config = isCrypto ? CRYPTO_PERIODS[period] : PERIODS[period];

    // Crypto API requires 'market' parameter
    let url = `${API_BASE}?function=${config.function}&symbol=${symbol}&apikey=${API_KEY}`;

    if (isCrypto) {
        url += `&market=USD`;
    }

    if (config.interval) {
        url += `&interval=${config.interval}`;
    }

    if (config.function === 'TIME_SERIES_DAILY' && period === '1Y') {
        url += '&outputsize=full';
    }

    try {
        console.log(`Fetching from API: ${symbol} (${period}) [${state.currentMode}]`);
        await incrementApiUsage();

        const response = await fetch(url);
        const data = await response.json();

        if (data['Error Message']) {
            throw new Error('無効なシンボルです。ティッカーシンボルを確認してください。');
        }

        if (data['Note']) {
            throw new Error('API制限に達しました。キャッシュデータを使用してください。');
        }

        if (data['Information']) {
            throw new Error('API情報: ' + data['Information']);
        }

        const parsedData = isCrypto
            ? parseCryptoData(data, config, period, symbol)
            : parseStockData(data, config, period, symbol);

        setCachedData(symbol, period, parsedData);
        return parsedData;
    } catch (error) {
        const cachedFallback = getCachedData(symbol, period);
        if (cachedFallback) {
            console.log('API failed, using cached data');
            return cachedFallback;
        }

        if (error.message.includes('fetch')) {
            throw new Error('ネットワークエラーが発生しました。接続を確認してください。');
        }
        throw error;
    }
}

function parseStockData(data, config, period, symbol) {
    let timeSeriesKey;

    if (config.function === 'TIME_SERIES_INTRADAY') {
        timeSeriesKey = `Time Series (${config.interval})`;
    } else {
        timeSeriesKey = 'Time Series (Daily)';
    }

    const timeSeries = data[timeSeriesKey];
    if (!timeSeries) {
        throw new Error('データが見つかりません。');
    }

    const entries = Object.entries(timeSeries).slice(0, config.limit).reverse();

    const candleData = [];
    const volumeData = [];

    entries.forEach(([dateStr, values]) => {
        const time = dateStr.split(' ')[0];

        const open = parseFloat(values['1. open']);
        const high = parseFloat(values['2. high']);
        const low = parseFloat(values['3. low']);
        const close = parseFloat(values['4. close']);
        const volume = parseInt(values['5. volume']);

        candleData.push({ time, open, high, low, close });
        volumeData.push({
            time,
            value: volume,
            color: close >= open ? 'rgba(38, 166, 65, 0.5)' : 'rgba(248, 81, 73, 0.5)',
        });
    });

    const latest = candleData[candleData.length - 1];
    const previous = candleData.length > 1 ? candleData[candleData.length - 2] : latest;

    const priceChange = latest.close - previous.close;
    const changePercent = (priceChange / previous.close) * 100;


    return {
        symbol: symbol.toUpperCase(),
        company: getCompanyName(symbol),
        currentPrice: latest.close,
        change: priceChange,
        changePercent,
        open: latest.open,
        high: Math.max(...candleData.map(c => c.high)),
        low: Math.min(...candleData.map(c => c.low)),
        volume: volumeData[volumeData.length - 1].value,
        candleData,
        volumeData,
    };
}

function parseCryptoData(data, config, period, symbol) {
    const timeSeriesKey = 'Time Series (Digital Currency Daily)';
    const timeSeries = data[timeSeriesKey];

    if (!timeSeries) {
        throw new Error('仮想通貨データが見つかりません (API Response Key missing)。');
    }

    // Limit data points based on period config
    const entries = Object.entries(timeSeries).slice(0, config.limit).reverse();

    const candleData = [];
    const volumeData = [];

    // Helper to find value by keyword (robust to 1a vs 1b etc)
    const getValue = (obj, type) => {
        const keys = Object.keys(obj);
        // Prioritize USD keys
        let key = keys.find(k => k.toLowerCase().includes(type) && k.includes('USD'));
        if (!key) {
            // Fallback to any key with the type
            key = keys.find(k => k.toLowerCase().includes(type));
        }
        return key ? parseFloat(obj[key]) : 0;
    };

    entries.forEach(([dateStr, values]) => {
        const time = dateStr.split(' ')[0];

        const open = getValue(values, 'open');
        const high = getValue(values, 'high');
        const low = getValue(values, 'low');
        const close = getValue(values, 'close');
        const volume = getValue(values, 'volume');

        // Sanity check
        if (isNaN(close)) {
            console.warn(`NaN detected for ${time}`, values);
        }

        candleData.push({ time, open, high, low, close });
        volumeData.push({
            time,
            value: volume,
            color: close >= open ? 'rgba(38, 166, 65, 0.5)' : 'rgba(248, 81, 73, 0.5)',
        });
    });

    const latest = candleData[candleData.length - 1];
    const previous = candleData.length > 1 ? candleData[candleData.length - 2] : latest;

    const priceChange = (latest && previous) ? latest.close - previous.close : 0;
    const changePercent = (previous && previous.close) ? (priceChange / previous.close) * 100 : 0;

    return {
        symbol: symbol.toUpperCase(),
        company: getCompanyName(symbol),
        currentPrice: latest ? latest.close : 0,
        change: priceChange,
        changePercent: changePercent || 0, // Ensure no NaN
        open: latest ? latest.open : 0,
        high: candleData.length > 0 ? Math.max(...candleData.map(c => c.high)) : 0,
        low: candleData.length > 0 ? Math.min(...candleData.map(c => c.low)) : 0,
        volume: volumeData.length > 0 ? volumeData[volumeData.length - 1].value : 0,
        candleData,
        volumeData,
    };
}
